import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDivisionGroups } from '../../../domain/divisions/divisionsQueries'
import {
  FilterCategory,
  FiltersState,
  STORAGE_KEYS,
  projectTypeDefaults,
  scheduledStatusDefaults,
  invoicedStatusDefaults,
  visitReviewStatusDefaults,
  emptyDivisionsFilter,
  buildStaticFilter,
  buildDivisionsFilter,
  updateCategory,
  serializeFilter,
  parseDateRange,
  serializeDateRange,
  DateRange,
  FilterStorageKeys,
} from './filtersModel'

export type InvoiceByJobFilterName = 'projectType' | 'scheduledStatus' | 'invoicedStatus' | 'divisions' | 'visitReviewStatus'

export type InvoiceByJobFilters = {
  state: FiltersState
  setSearch: (search: string) => void
  setCategory: (filterName: InvoiceByJobFilterName, selectedValues: (number | string)[]) => void
  setDateRange: (startDate: string | null, endDate: string | null) => void
}

export const defaultInvoiceByJobFilters = (): InvoiceByJobFilters => ({
  state: {
    projectType: buildStaticFilter('projectType', 'Project Type', projectTypeDefaults, null),
    scheduledStatus: buildStaticFilter('scheduledStatus', 'Status', scheduledStatusDefaults, null),
    invoicedStatus: buildStaticFilter('invoicedStatus', 'Reviewed/Not-Reviewed', invoicedStatusDefaults, null),
    divisions: emptyDivisionsFilter,
    visitReviewStatus: buildStaticFilter('visitReviewStatus', 'Visit Review Status', visitReviewStatusDefaults, null),
    dateRange: { starting: null, ending: null },
    search: '',
  },
  setSearch: () => { },
  setCategory: () => { },
  setDateRange: () => { },
})

export type FilterOptions = {
  storageKeys?: FilterStorageKeys
  searchDebounceMs?: number
}

/**
 * Owns the Jobs to Invoice filter state so more than one filter UI (the list
 * toolbar and the editor drawer sidebar) can read and change the same filters.
 */
export const useInvoiceByJobFilters = ({
  storageKeys = STORAGE_KEYS,
  searchDebounceMs = 0,
}: FilterOptions = {}) => {
  const divisionGroupsQuery = useDivisionGroups()
  const divisionGroups = divisionGroupsQuery.data || []
  const [search, setSearchState] = useState(() => storageKeys.search ? localStorage.getItem(storageKeys.search) ?? '' : '')

  const [projectType, setProjectType] = useState<FilterCategory>(() =>
    buildStaticFilter('projectType', 'Project Type', projectTypeDefaults, localStorage.getItem(storageKeys.projectType))
  )
  const [scheduledStatus, setScheduledStatus] = useState<FilterCategory>(() =>
    buildStaticFilter('scheduledStatus', 'Status', scheduledStatusDefaults, localStorage.getItem(storageKeys.scheduledStatus))
  )
  const [invoicedStatus, setInvoicedStatus] = useState<FilterCategory>(() =>
    buildStaticFilter('invoicedStatus', 'Reviewed/Not-Reviewed', invoicedStatusDefaults, localStorage.getItem(storageKeys.invoicedStatus))
  )
  const [divisionsState, setDivisionsState] = useState<FilterCategory>(emptyDivisionsFilter)
  const [visitReviewStatus, setVisitReviewStatus] = useState<FilterCategory>(() =>
    buildStaticFilter('visitReviewStatus', 'Visit Review Status', visitReviewStatusDefaults, storageKeys.visitReviewStatus ? localStorage.getItem(storageKeys.visitReviewStatus) : null)
  )
  const [dateRange, setDateRangeState] = useState<DateRange>(() =>
    parseDateRange(localStorage.getItem(storageKeys.dateRange))
  )

  const [debouncedSearch, setDebouncedSearch] = useState(search)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), searchDebounceMs)
    return () => clearTimeout(timer)
  }, [search, searchDebounceMs])

  const state = useMemo<FiltersState>(() => ({ projectType, scheduledStatus, invoicedStatus, divisions: divisionsState, visitReviewStatus, dateRange, search }),
    [projectType, scheduledStatus, invoicedStatus, divisionsState, visitReviewStatus, dateRange, search])
  const appliedSearch = searchDebounceMs ? debouncedSearch : search
  const appliedState = useMemo(() => ({ projectType, scheduledStatus, invoicedStatus, divisions: divisionsState, visitReviewStatus, dateRange, search: appliedSearch }),
    [projectType, scheduledStatus, invoicedStatus, divisionsState, visitReviewStatus, dateRange, appliedSearch])

  useEffect(() => {
    if (divisionGroups.length > 0 && divisionsState.values.length === 0) {
      const divisions = buildDivisionsFilter(
        divisionGroups.map(d => d.groupName),
        localStorage.getItem(storageKeys.divisions)
      )
      setDivisionsState(divisions)
    }
  }, [divisionGroups])

  const setSearch = useCallback((nextSearch: string) => {
    setSearchState(nextSearch)
    if (storageKeys.search) {
      localStorage.setItem(storageKeys.search, nextSearch)
    }
  }, [storageKeys.search])

  const setCategory = useCallback((filterName: InvoiceByJobFilterName, selectedValues: (number | string)[]) => {
    if (filterName === 'projectType') {
      const updated = updateCategory(projectType, selectedValues)
      localStorage.setItem(storageKeys.projectType, serializeFilter(updated))
      setProjectType(updated)
    } else if (filterName === 'scheduledStatus') {
      const updated = updateCategory(scheduledStatus, selectedValues)
      localStorage.setItem(storageKeys.scheduledStatus, serializeFilter(updated))
      setScheduledStatus(updated)
    } else if (filterName === 'invoicedStatus') {
      const updated = updateCategory(invoicedStatus, selectedValues)
      localStorage.setItem(storageKeys.invoicedStatus, serializeFilter(updated))
      setInvoicedStatus(updated)
    } else if (filterName === 'divisions') {
      const updated = updateCategory(divisionsState, selectedValues)
      localStorage.setItem(storageKeys.divisions, serializeFilter(updated))
      setDivisionsState(updated)
    } else if (filterName === 'visitReviewStatus') {
      const updated = updateCategory(visitReviewStatus, selectedValues)
      if (storageKeys.visitReviewStatus) {
        localStorage.setItem(storageKeys.visitReviewStatus, serializeFilter(updated))
      }
      setVisitReviewStatus(updated)
    }
  }, [projectType, scheduledStatus, invoicedStatus, divisionsState, visitReviewStatus, storageKeys])

  const setDateRange = useCallback((startDate: string | null, endDate: string | null) => {
    const newRange = { starting: startDate, ending: endDate }
    setDateRangeState(newRange)
    localStorage.setItem(storageKeys.dateRange, serializeDateRange(newRange))
  }, [storageKeys.dateRange])

  return useMemo(() => ({ state, appliedState, setSearch, setCategory, setDateRange }), [state, appliedState, setSearch, setCategory, setDateRange])
}
